import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Users,
  MapPin,
  Ban,
  ShieldAlert,
  Move,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

import AdminPageHeader from "@/components/layout/AdminPageHeader";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CAMBIO_MESA } from "@/constants/testIds";
import {
  listarComprasConfirmadas,
  listarOcupacionMesas,
  cambiarMesaCompra,
  puedeGestionarMesas,
} from "@/data/adminCambioMesaStore";
import { getAdminSession } from "@/data/adminAuthStore";

const formatearMoneda = (v) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
  }).format(Number(v) || 0);

const badgeMesaClasses = (estado, vendible) => {
  const e = String(estado || "").toUpperCase();
  if (!vendible) {
    if (e === "BLOQUEADA")
      return "bg-destructive/10 text-destructive border-destructive/40";
    if (e === "PROTOCOLO")
      return "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/40";
    return "bg-muted text-muted-foreground border-border/70";
  }
  return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/40";
};

const traducirErrorCambio = (res) => {
  const status = res?.status;
  const err = String(res?.error || "");
  if (status === 403) return "Tu rol no tiene permiso para cambiar la mesa.";
  if (status === 401) return "Tu sesión expiró. Vuelve a iniciar sesión.";
  if (status === 404) {
    if (/mesa/i.test(err)) return err;
    return "Compra o evento no encontrados.";
  }
  if (status === 409) return err || "La operación no es válida en este estado.";
  if (err === "network") return "Sin conexión con el servidor. Intenta de nuevo.";
  return err || "No se pudo cambiar la mesa.";
};

export default function CambioMesa() {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const session = getAdminSession();
  const autorizado = puedeGestionarMesas(session?.rol);

  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [compras, setCompras] = useState([]);
  const [eventoNombre, setEventoNombre] = useState("");
  const [ocupacion, setOcupacion] = useState([]);
  const [errorCarga, setErrorCarga] = useState("");

  // Diálogo cambio
  const [dialogOpen, setDialogOpen] = useState(false);
  const [compraSel, setCompraSel] = useState(null);
  const [mesaDestino, setMesaDestino] = useState("");
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [errorDialog, setErrorDialog] = useState("");
  const [confirmarOpen, setConfirmarOpen] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setErrorCarga("");
    const [confRes, ocupRes] = await Promise.all([
      listarComprasConfirmadas(eventoId),
      listarOcupacionMesas(eventoId),
    ]);
    let hubo = false;
    if (confRes && !confRes.error) {
      setCompras(confRes.compras || []);
      setEventoNombre(confRes.eventoNombre || "");
    } else {
      hubo = true;
    }
    if (ocupRes && !ocupRes.error) {
      setOcupacion(ocupRes.mesas || []);
    } else {
      hubo = true;
    }
    if (hubo) {
      const status = confRes?.status || ocupRes?.status;
      if (status === 403)
        setErrorCarga("Tu rol no tiene permiso para gestionar mesas.");
      else if (status === 404) setErrorCarga("Evento no encontrado.");
      else setErrorCarga("No se pudo cargar la información. Intenta refrescar.");
    }
    setLoading(false);
  }, [eventoId]);

  useEffect(() => {
    if (!autorizado) return;
    cargar();
  }, [cargar, autorizado, refreshTick]);

  const ocupacionPorMesa = useMemo(() => {
    const map = new Map();
    for (const m of ocupacion) map.set(Number(m.numero), m);
    return map;
  }, [ocupacion]);

  /** Mesas destino candidatas para una compra: DISPONIBLE + capacidad
   *  restante ≥ compra.cantidad, excluye la mesa actual. */
  const mesasDestinoValidas = useMemo(() => {
    if (!compraSel) return [];
    const cantidad = Number(compraSel.cantidad || 0);
    return ocupacion
      .filter((m) => {
        if (Number(m.numero) === Number(compraSel.mesa)) return false;
        if (!m.vendible) return false;
        return Number(m.disponibles || 0) >= cantidad;
      })
      .sort((a, b) => Number(a.numero) - Number(b.numero));
  }, [compraSel, ocupacion]);

  const abrirDialogo = (compra) => {
    setCompraSel(compra);
    setMesaDestino("");
    setMotivo("");
    setErrorDialog("");
    setEnviando(false);
    setDialogOpen(true);
  };

  const cerrarDialogo = () => {
    setDialogOpen(false);
    setConfirmarOpen(false);
    setCompraSel(null);
    setMesaDestino("");
    setMotivo("");
    setErrorDialog("");
    setEnviando(false);
  };

  const solicitarConfirmacion = () => {
    if (!mesaDestino) {
      setErrorDialog("Selecciona una mesa destino.");
      return;
    }
    setErrorDialog("");
    setConfirmarOpen(true);
  };

  const ejecutarCambio = async () => {
    if (!compraSel || !mesaDestino) return;
    setEnviando(true);
    setErrorDialog("");
    const res = await cambiarMesaCompra(
      compraSel.id,
      Number(mesaDestino),
      motivo
    );
    setEnviando(false);
    if (!res || res.error) {
      const msg = traducirErrorCambio(res || {});
      setErrorDialog(msg);
      setConfirmarOpen(false);
      // toast también para visibilidad
      toast.error(msg);
      // Refrescar por si la razón fue capacidad ya tomada por otro
      setRefreshTick((t) => t + 1);
      return;
    }
    if (res.cambio === false) {
      toast.info("La mesa destino es la misma; no hubo cambio.");
    } else {
      toast.success(
        `Mesa cambiada: ${res.mesaAnterior} → ${res.mesaNueva}. Socio notificado.`
      );
    }
    cerrarDialogo();
    setRefreshTick((t) => t + 1);
  };

  /* ---------- RBAC hard gate ---------- */
  if (!autorizado) {
    return (
      <div
        data-testid={CAMBIO_MESA.screen}
        className="flex flex-col gap-6 max-w-2xl"
      >
        <AdminPageHeader
          eyebrow="Portal administrativo · Gestión de mesas"
          title="Acceso restringido"
          description="Sólo Gerencia, Sistemas y Eventos pueden gestionar los cambios de mesa de compras confirmadas."
        />
        <div className="rounded-2xl border hairline bg-card px-5 py-4 flex items-center gap-3 text-sm">
          <ShieldAlert className="w-5 h-5 text-destructive" />
          <p>
            Tu rol actual (<b>{session?.rol || "—"}</b>) no tiene permiso para
            operar esta función.
          </p>
        </div>
        <Button asChild variant="outline" className="w-fit">
          <Link to="/admin/eventos" data-testid={CAMBIO_MESA.btnRegresar}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Regresar a Eventos
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid={CAMBIO_MESA.screen} className="flex flex-col gap-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <AdminPageHeader
            eyebrow="Portal administrativo · Gestión de mesas"
            title="Cambio de mesa"
            description={
              eventoNombre ? (
                <>
                  Evento:{" "}
                  <span
                    className="text-foreground font-medium"
                    data-testid={CAMBIO_MESA.eventoNombre}
                  >
                    {eventoNombre}
                  </span>
                </>
              ) : (
                "Selecciona una compra confirmada para reasignarla."
              )
            }
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setRefreshTick((t) => t + 1)}
            data-testid={CAMBIO_MESA.btnRefrescar}
            disabled={loading}
            className="h-10 rounded-xl border-border/70 bg-card"
          >
            <RefreshCw
              className={cn("w-4 h-4 mr-2", loading && "animate-spin")}
            />
            Refrescar
          </Button>
          <Button asChild variant="ghost" className="h-10 rounded-xl">
            <Link to="/admin/eventos" data-testid={CAMBIO_MESA.btnRegresar}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Regresar
            </Link>
          </Button>
        </div>
      </div>

      {errorCarga && (
        <div
          data-testid={CAMBIO_MESA.comprasError}
          className="rounded-xl border hairline bg-destructive/5 text-destructive px-4 py-3 flex items-center gap-2 text-sm"
        >
          <AlertTriangle className="w-4 h-4" />
          {errorCarga}
        </div>
      )}

      {/* Ocupación por mesa */}
      <section className="flex flex-col gap-4">
        <header className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-accent" />
          <h2 className="font-heading text-lg">Ocupación por mesa</h2>
        </header>
        {ocupacion.length === 0 && !loading ? (
          <p className="text-sm text-muted-foreground">
            El evento no tiene mesas configuradas.
          </p>
        ) : (
          <div
            data-testid={CAMBIO_MESA.ocupacionGrid}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
          >
            {ocupacion.map((m) => (
              <div
                key={m.numero}
                data-testid={CAMBIO_MESA.ocupacionMesa(m.numero)}
                className="rounded-xl border hairline bg-card px-3 py-3 flex flex-col gap-1.5"
              >
                <div className="flex items-center justify-between">
                  <span className="font-heading text-lg leading-none">
                    Mesa {m.numero}
                  </span>
                  <span
                    className={cn(
                      "text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full border",
                      badgeMesaClasses(m.estado, m.vendible)
                    )}
                  >
                    {m.estado}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  <span className="tabular-nums">
                    {m.ocupados}/{m.capacidad}
                  </span>
                  <span>·</span>
                  <span className="tabular-nums">
                    {m.disponibles} libres
                  </span>
                </div>
                {m.tipo && (
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    {m.tipo}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Compras confirmadas */}
      <section className="flex flex-col gap-4">
        <header className="flex items-center gap-2">
          <Move className="w-4 h-4 text-accent" />
          <h2 className="font-heading text-lg">Compras confirmadas</h2>
        </header>

        {compras.length === 0 && !loading ? (
          <EmptyState
            icon={Ban}
            eyebrow="Sin compras confirmadas"
            title="Este evento no tiene compras confirmadas"
            description="Sólo compras en estado CONFIRMADA admiten cambio de mesa."
            testId={CAMBIO_MESA.comprasEmpty}
          />
        ) : (
          <div
            data-testid={CAMBIO_MESA.comprasTable}
            className="rounded-2xl border hairline bg-card overflow-hidden"
          >
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted/40">
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Socio</TableHead>
                    <TableHead>Nº Socio</TableHead>
                    <TableHead className="text-right">Mesa actual</TableHead>
                    <TableHead className="text-right">Boletos</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead className="w-40 text-right">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {compras.map((c) => (
                    <TableRow
                      key={c.id}
                      data-testid={CAMBIO_MESA.compraRow(c.id)}
                    >
                      <TableCell className="max-w-[16rem]">
                        <p className="font-medium truncate">
                          {c.nombreSocio || "—"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {c.id}
                        </p>
                      </TableCell>
                      <TableCell className="tabular-nums text-sm">
                        {c.socioId}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {c.mesa}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {c.cantidad}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {formatearMoneda(c.total)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[10rem] truncate">
                        {c.referencia || "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          onClick={() => abrirDialogo(c)}
                          data-testid={CAMBIO_MESA.compraCambiarBtn(c.id)}
                          className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                          <Move className="w-3.5 h-3.5 mr-1.5" />
                          Cambiar mesa
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </section>

      {/* ---- Diálogo cambio de mesa ---- */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => (!o ? cerrarDialogo() : null)}
      >
        <DialogContent
          data-testid={CAMBIO_MESA.dialog}
          className="max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Move className="w-4 h-4 text-accent" /> Cambiar mesa
            </DialogTitle>
            <DialogDescription>
              Reasigna la compra a otra mesa disponible del mismo evento. Los
              QR de los boletos permanecen intactos.
            </DialogDescription>
          </DialogHeader>

          {compraSel && (
            <div className="flex flex-col gap-4">
              <div className="rounded-lg border hairline bg-muted/30 px-3 py-3 text-sm">
                <p>
                  <span className="text-muted-foreground">Socio:</span>{" "}
                  <b>{compraSel.nombreSocio || "—"}</b> ({compraSel.socioId})
                </p>
                <p
                  className="mt-1"
                  data-testid={CAMBIO_MESA.dialogMesaActual}
                >
                  <span className="text-muted-foreground">Mesa actual:</span>{" "}
                  <b>{compraSel.mesa}</b>
                  <span className="ml-3 text-muted-foreground">Boletos:</span>{" "}
                  <b>{compraSel.cantidad}</b>
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Mesa destino *</Label>
                {mesasDestinoValidas.length === 0 ? (
                  <div
                    data-testid={CAMBIO_MESA.dialogSinDestinos}
                    className="rounded-lg border hairline bg-amber-500/10 text-amber-800 dark:text-amber-300 px-3 py-2 text-xs flex items-start gap-2"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      No hay mesas disponibles con capacidad para{" "}
                      {compraSel.cantidad} boleto(s). Refresca o libera cupo
                      en el plano.
                    </span>
                  </div>
                ) : (
                  <Select
                    value={mesaDestino}
                    onValueChange={setMesaDestino}
                  >
                    <SelectTrigger
                      data-testid={CAMBIO_MESA.dialogMesaSelect}
                      className="bg-card"
                    >
                      <SelectValue placeholder="Selecciona una mesa" />
                    </SelectTrigger>
                    <SelectContent>
                      {mesasDestinoValidas.map((m) => (
                        <SelectItem
                          key={m.numero}
                          value={String(m.numero)}
                          data-testid={CAMBIO_MESA.dialogMesaOption(m.numero)}
                        >
                          Mesa {m.numero} · {m.disponibles} lugares libres ·
                          cap {m.capacidad}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Label>Motivo (opcional)</Label>
                <Textarea
                  data-testid={CAMBIO_MESA.dialogMotivo}
                  rows={2}
                  maxLength={240}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ej. Solicitud del socio, ubicación preferente, etc."
                  className="resize-none"
                />
              </div>

              {errorDialog && (
                <div
                  data-testid={CAMBIO_MESA.dialogError}
                  className="rounded-lg border hairline bg-destructive/5 text-destructive px-3 py-2 text-xs flex items-start gap-2"
                >
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{errorDialog}</span>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={cerrarDialogo}
              data-testid={CAMBIO_MESA.dialogCancelar}
              disabled={enviando}
            >
              Cancelar
            </Button>
            <Button
              data-testid={CAMBIO_MESA.dialogConfirmar}
              disabled={
                enviando ||
                !mesaDestino ||
                mesasDestinoValidas.length === 0
              }
              onClick={solicitarConfirmacion}
              className="bg-primary text-primary-foreground"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación explícita antes de ejecutar */}
      <Dialog
        open={confirmarOpen}
        onOpenChange={(o) => (!o && !enviando ? setConfirmarOpen(false) : null)}
      >
        <DialogContent
          data-testid={CAMBIO_MESA.dialogConfirmMesa}
          className="max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Confirmar cambio
            </DialogTitle>
            <DialogDescription>
              {compraSel && (
                <>
                  Se cambiará la compra{" "}
                  <b>{compraSel.nombreSocio || compraSel.socioId}</b> de la{" "}
                  <b>Mesa {compraSel.mesa}</b> a la{" "}
                  <b>Mesa {mesaDestino}</b>. Se notificará al socio
                  automáticamente.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmarOpen(false)}
              data-testid={CAMBIO_MESA.dialogConfirmNo}
              disabled={enviando}
            >
              Regresar
            </Button>
            <Button
              onClick={ejecutarCambio}
              data-testid={CAMBIO_MESA.dialogConfirmSi}
              disabled={enviando}
              className="bg-primary text-primary-foreground"
            >
              {enviando ? "Aplicando…" : "Sí, cambiar la mesa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
